/**
 * ACH 竞争性假设分析（Analysis of Competing Hypotheses）。
 * 核心原则：不以「支持某假设」取胜，而以「被否证最少」取胜。
 * 纯函数，便于测试。
 */

export type AchRating = '++' | '+' | 'NA' | '-' | '--';

export const ACH_RATINGS: AchRating[] = ['++', '+', 'NA', '-', '--'];

export const ACH_RATING_META: Record<
  AchRating,
  { label: string; short: string; weight: number; tone: 'pro' | 'neutral' | 'con' }
> = {
  '++': { label: '强烈一致', short: '++', weight: -2, tone: 'pro' },
  '+': { label: '一致', short: '+', weight: -1, tone: 'pro' },
  NA: { label: '不适用', short: 'N/A', weight: 0, tone: 'neutral' },
  '-': { label: '不一致', short: '−', weight: 1, tone: 'con' },
  '--': { label: '强烈不一致', short: '−−', weight: 2, tone: 'con' },
};

export type AchCell = {
  hypothesisId: string;
  evidenceId: string;
  rating: AchRating;
  note?: string | null;
};

export type AchHypothesisInput = {
  id: string;
  statement: string;
  status: string;
  confidence: number;
};

export type AchEvidenceInput = {
  id: string;
  title: string | null;
  kind: string;
  sourceReliability?: string;
  infoCredibility?: number;
};

export type AchHypothesisScore = {
  hypothesisId: string;
  statement: string;
  /** 否证分：越低越好 */
  disconfirmScore: number;
  strongInconsistent: number;
  inconsistent: number;
  consistent: number;
  strongConsistent: number;
  notApplicable: number;
  /** 参与评分的证据条数 */
  rated: number;
  /** 诊断价值：与该假设相关（非 N/A）且能区分假设的证据条数 */
  diagnosticEvidence: number;
  rank: number;
  verdict: 'favored' | 'possible' | 'unlikely';
};

export type AchEvidenceScore = {
  evidenceId: string;
  /** 该证据在不同假设间是否给出不同评级：0 = 无诊断价值 */
  diagnosticValue: number;
  consistencySpread: number;
  notApplicable: number;
};

export type AchResult = {
  hypothesisScores: AchHypothesisScore[];
  evidenceScores: AchEvidenceScore[];
  /** 有诊断价值（能区分假设）的证据 id */
  keyEvidence: string[];
  /** 无诊断价值的证据：一致或不适用，不能帮助判断 */
  nonDiagnosticEvidence: string[];
  totalCells: number;
  filledCells: number;
};

function weight(rating: AchRating): number {
  return ACH_RATING_META[rating]?.weight ?? 0;
}

/**
 * 计算 ACH 矩阵结果。
 * @param cells 已填写的评级单元格
 */
export function analyzeAch(
  cells: AchCell[],
  hypotheses: AchHypothesisInput[],
  evidence: AchEvidenceInput[]
): AchResult {
  const cellIndex = new Map<string, AchCell>();
  for (const cell of cells) cellIndex.set(`${cell.hypothesisId}::${cell.evidenceId}`, cell);

  const evidenceScores: AchEvidenceScore[] = [];
  const keyEvidence: string[] = [];
  const nonDiagnosticEvidence: string[] = [];

  for (const item of evidence) {
    const ratings: AchRating[] = [];
    let na = 0;
    for (const hypothesis of hypotheses) {
      const cell = cellIndex.get(`${hypothesis.id}::${item.id}`);
      if (!cell) continue;
      ratings.push(cell.rating);
      if (cell.rating === 'NA') na += 1;
    }
    const applicable = ratings.filter(rating => rating !== 'NA');
    const distinct = new Set(applicable).size;
    const spread = applicable.length === 0 ? 0 : Math.max(...applicable.map(weight)) - Math.min(...applicable.map(weight));

    evidenceScores.push({
      evidenceId: item.id,
      diagnosticValue: distinct,
      consistencySpread: spread,
      notApplicable: na,
    });

    // 有诊断价值：至少对两个假设给出不同评级，且不全是 N/A
    if (distinct >= 2 && applicable.length >= 2) keyEvidence.push(item.id);
    else if (ratings.length > 0) nonDiagnosticEvidence.push(item.id);
  }

  const hypothesisScores: AchHypothesisScore[] = hypotheses.map(hypothesis => {
    let disconfirmScore = 0;
    let strongInconsistent = 0;
    let inconsistent = 0;
    let consistent = 0;
    let strongConsistent = 0;
    let notApplicable = 0;
    let rated = 0;
    let diagnosticEvidence = 0;

    for (const item of evidence) {
      const cell = cellIndex.get(`${hypothesis.id}::${item.id}`);
      if (!cell) continue;
      rated += 1;
      switch (cell.rating) {
        case '--':
          strongInconsistent += 1;
          disconfirmScore += 2;
          diagnosticEvidence += 1;
          break;
        case '-':
          inconsistent += 1;
          disconfirmScore += 1;
          diagnosticEvidence += 1;
          break;
        case '+':
          consistent += 1;
          break;
        case '++':
          strongConsistent += 1;
          break;
        default:
          notApplicable += 1;
          break;
      }
    }

    return {
      hypothesisId: hypothesis.id,
      statement: hypothesis.statement,
      disconfirmScore,
      strongInconsistent,
      inconsistent,
      consistent,
      strongConsistent,
      notApplicable,
      rated,
      diagnosticEvidence,
      rank: 0,
      verdict: 'possible',
    };
  });

  // 排序：否证分升序 → 强不一致升序 → 一致证据降序
  const ordered = [...hypothesisScores].sort(
    (a, b) =>
      a.disconfirmScore - b.disconfirmScore ||
      a.strongInconsistent - b.strongInconsistent ||
      b.strongConsistent + b.consistent - (a.strongConsistent + a.consistent)
  );
  ordered.forEach((score, index) => {
    score.rank = index + 1;
    if (index === 0) score.verdict = score.disconfirmScore === 0 ? 'favored' : 'possible';
    else score.verdict = score.strongInconsistent >= 2 ? 'unlikely' : 'possible';
  });

  return {
    hypothesisScores: ordered,
    evidenceScores,
    keyEvidence,
    nonDiagnosticEvidence,
    totalCells: hypotheses.length * evidence.length,
    filledCells: cells.length,
  };
}

/** 从研判条目已有的支持/反证关系推断初始评级。 */
export function seedRatingsFromHypotheses(
  hypotheses: (AchHypothesisInput & { supporting?: string[]; contradicting?: string[] })[],
  evidenceIds: string[]
): AchCell[] {
  const cells: AchCell[] = [];
  for (const hypothesis of hypotheses) {
    for (const evidenceId of evidenceIds) {
      let rating: AchRating = 'NA';
      if (hypothesis.supporting?.includes(evidenceId)) rating = '++';
      else if (hypothesis.contradicting?.includes(evidenceId)) rating = '--';
      cells.push({ hypothesisId: hypothesis.id, evidenceId, rating });
    }
  }
  return cells;
}

export function emptyMatrix(cells: AchCell[], hypotheses: string[], evidence: string[]): number {
  const filled = new Set(cells.map(cell => `${cell.hypothesisId}::${cell.evidenceId}`));
  let missing = 0;
  for (const h of hypotheses) for (const e of evidence) if (!filled.has(`${h}::${e}`)) missing += 1;
  return missing;
}